import { IFolderData } from "common/records/Folder";
import { IFolderResponse, byName, destroy, list, updateOrCreate } from "common/api/folder.service";
import { DANGER, SUCCESS, addFlashAlert } from "common/components/FlashAlert";
import { RootState, ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { FolderActions } from "common/reducers/folder.actions";
import { IdsManagementActions } from "common/reducers/idsManagement.actions";
import { folder } from "common/selectors/folder.selectors";
import { dirtyIds } from "common/selectors/idsManagement.selectors";
import { currentJobId } from "common/selectors/job.selectors";
import { currentOrgId } from "common/selectors/org.selectors";
import { RootDispatchType } from "common/store";
import { AssociationType, JOB, Nullable, ORG } from "common/types";
import { List } from "immutable";
import { PresentationAsyncActions } from "common/reducers/presentation.async.actions";
import { FileType } from "common/components/Folders";
import { DocumentAsyncActions } from "common/reducers/document.async.actions";
import { FolderParamsRecord } from "common/records/index/FolderParams";
import { ImageAsyncActions } from "common/reducers/image.async.actions";
import { EstimateTemplateAsyncActions } from "common/reducers/estimateTemplate.async.actions";

export const FolderAsyncActions = {
  listFolders: (queryParams: FolderParamsRecord): ThunkResult<Promise<void | { redirect: boolean }>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      queryParams = setOptionsFolderId(queryParams, getState());

      // Guard against a null/invalid folder id (a programmer error, e.g. listing before the folder
      // tree resolved). Return silently — this branch is not user-facing, so it must not toast.
      if (!queryParams.id || queryParams.id <= 0) {
        return;
      }

      dispatch(IdsManagementActions.fetchModel("folders", queryParams));

      try {
        const response = await list(queryParams);
        dispatch(FolderActions.receiveFolders(response.folders));
        dispatch(FolderActions.receiveFolders(response.parents));

        const ids = List(response.folders.map((folder) => folder.id));
        dispatch(IdsManagementActions.receiveModel("folders", ids));
      } catch (response: any) {
        dispatch(IdsManagementActions.receiveModel("folders", List()));
        // rsf-api answers 404 when the parent folder was deleted; that's an empty list here, not
        // an error to flash.
        if (response?.status !== 404) {
          handleErrors(response);
        }
      }
    };
  },
  /**
   * Resolves a folderable's root folder id by name straight from the API (rsf-app `listFoldersByName`
   * parity), instead of watching the store for a value that may never arrive. The Org "Estimate
   * Templates" folder is a child of the org root — not one of the org record's typed root folders — so
   * a store watch for it can 20s-time-out and return null. Returns the folder id, or null when it
   * doesn't exist (server 204) or the request fails.
   */
  resolveRootFolderId: (
    folderName: string,
    folderableType: AssociationType = ORG,
  ): ThunkResult<Promise<Nullable<number>>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      const state = getState();
      const folderableId = folderableType === ORG ? currentOrgId(state) : currentJobId(state);
      if (!folderableId) return null;

      try {
        const response = await byName({
          folderable_id: folderableId,
          folderable_type: folderableType,
          name: folderName,
        });
        // `parents[0]` is the named folder itself (self_and_ancestors, self-first). A 204 resolves to an
        // empty body, so guard the whole chain.
        return response?.parents?.[0]?.id ?? null;
      } catch (response) {
        handleErrors(response);
        return null;
      }
    };
  },
  destroyFolder: (folderId: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType) => {
      dispatch(FolderActions.fetchFolder(folderId));

      try {
        await destroy(folderId);
        dispatch(FolderActions.removeFolder(folderId));
      } catch (response) {
        handleErrors(response);
        dispatch(FolderActions.setFolderLoaded(folderId));
      }
    };
  },
  onSortEnd: (oldIndex: number, newIndex: number): ThunkResult<Promise<void>> => {
    return async (dispatch: RootDispatchType, getState: () => RootState) => {
      dispatch(IdsManagementActions.onSortEnd("folders", oldIndex, newIndex));

      const ids = dirtyIds(getState()) as List<number>;
      if (!ids || ids.size === 0) {
        return;
      }
      await dispatch(FolderAsyncActions.batchUpdateOrCreateFolder(ids));

      dispatch(IdsManagementActions.setDirtyIds(null));
    };
  },
  batchUpdateOrCreateFolder: (folderIds: List<number>): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState) => {
      const promises: Promise<IFolderResponse>[] = [];

      folderIds.forEach((folderId) => {
        const updatedFolder = folder(getState(), { folderId });
        if (updatedFolder) {
          promises.push(updateOrCreate(updatedFolder));
        } else {
          console.error(`Folder ${folderId} not found`);
        }
      });

      try {
        const responses = await Promise.all(promises);
        const folders = responses.map((response) => response.folder);
        dispatch(FolderActions.receiveFolders(folders));
        folderIds.forEach((folderId, idx) => {
          if (folderId <= 0) {
            dispatch(IdsManagementActions.replaceId(folderId, folders[idx].id, "folders"));
          }
        });
        addFlashAlert(SUCCESS, "Saved folders successfully");
      } catch (response) {
        addFlashAlert(DANGER, "Sorting Failed, please try again.");
        console.error("Sorting Failed", response);
      }
    };
  },
  updateOrCreateFolder: (folderId: number): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType, getState) => {
      dispatch(FolderActions.fetchFolder(folderId));
      const updatedFolder = folder(getState(), { folderId });
      if (!updatedFolder) {
        console.error(`Folder ${folderId} not found`);
        addFlashAlert(DANGER, `Folder ${folderId} not found`);
        return;
      }

      try {
        const response = await updateOrCreate(updatedFolder);
        dispatch(FolderActions.receiveFolder(response.folder));
        if (folderId <= 0) {
          dispatch(IdsManagementActions.replaceId(folderId, response.folder.id, "folders"));
          dispatch(FolderActions.removeFolder(folderId));
        }

        addFlashAlert(SUCCESS, "Saved folder successfully");
      } catch (response) {
        handleErrors(response);
        dispatch(FolderActions.setFolderLoaded(folderId));
      }
    };
  },
  moveFiles: (folderId: number, fileType: FileType, modelName?: string): ThunkResult<Promise<any>> => {
    return async (dispatch: RootDispatchType) => {
      switch (fileType) {
        case "documents":
          dispatch(DocumentAsyncActions.moveDocuments(folderId, modelName));
          break;
        case "presentations":
          dispatch(PresentationAsyncActions.movePresentations(folderId));
          break;
        case "estimateTemplates":
          dispatch(EstimateTemplateAsyncActions.moveEstimateTemplates(folderId, modelName));
          break;
        case "images":
          dispatch(ImageAsyncActions.moveImages(folderId, modelName));
          break;
        default:
      }
    };
  },
};

export const setOptionsFolderId = (params: FolderParamsRecord, state: RootState): FolderParamsRecord => {
  if (!params.folderable_id) {
    switch (params.folderable_type) {
      case JOB:
        const jobId = currentJobId(state);
        if (jobId) {
          params = params.set("folderable_id", jobId);
        }
        break;
      case ORG:
        const orgId = currentOrgId(state);
        if (orgId) {
          params = params.set("folderable_id", orgId);
        }
        break;
      default:
    }
  }

  return params;
};

export const folderableCheck = (rootFolder: IFolderData, params: FolderParamsRecord): boolean => {
  return rootFolder.folderable_type === params.folderable_type && rootFolder.folderable_id === params.folderable_id;
};
